import { Component } from "@theme/component";

/**
 * Product Detail Tab Section Component
 *
 * @typedef {object} TabSectionRefs
 * @property {HTMLElement} tabSection - The main tab section element.
 * @extends Component<TabSectionRefs>
 */
export class TabSection extends Component {
  requiredRefs = ['tabSection', 'tabItem'];

  /** @type {Map<string, HTMLElement[]>} */
  #sectionMap = new Map();

  #initialTabName = '';

  /** @type {Element[]} */
  #tabItems = [];
  /** @type {HTMLElement|null} */
  #underline = null;

  get isMobile() {
    return window.innerWidth <= 768
  }
  connectedCallback() {
    super.connectedCallback();
    this.#initializeTabs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * 初始化标签页
   */
  #initializeTabs() {
    const tabItems = this.refs.tabSection.querySelectorAll('.tab-item');
    this.#tabItems = Array.from(tabItems).map(item => /** @type {HTMLElement} */(item));

    // 获取下划线元素
    this.#underline = this.refs.tabSection.querySelector('.tab-underline');
    // 初始化下划线到第一个激活tab
    const activeItem = /** @type {HTMLElement|null} */ (this.refs.tabSection.querySelector('.tab-item.active'));
    if (activeItem && this.#underline) {
      this.#moveUnderlineTo(activeItem);
    }

    // 构建区域映射
    this.#buildSectionMap();
  }

  /**
   * 构建区域映射
   */
  #buildSectionMap() {
    /** @type {NodeListOf<HTMLElement>} */
    const shopifySections = document.querySelectorAll('.tab-section-item');
    this.#tabItems.forEach((tab, index) => {
      const key = tab.getAttribute('id');
      if (!key) return;

      if (index === 0) {
        this.#initialTabName = key;
      }

      this.#sectionMap.set(key, []);

      shopifySections.forEach((section) => {
        if (section.querySelector(`[name="${key}"]`)) {
          const sections = this.#sectionMap.get(key) || [];
          sections.push(section);
          this.#sectionMap.set(key, sections);
          if (key !== this.#initialTabName) {
            section.style.display = "none";
          }
        }
      });
    });
  }

  /**
   * @description 移动下划线到指定tab
   * @param {HTMLElement} tab
   */
  #moveUnderlineTo(tab) {
    if (!this.#underline || !tab || !tab.parentElement) return;
    const parentRect = tab.parentElement.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const offsetX = tabRect.left - parentRect.left;
    this.#underline.style.transform = `translateX(${offsetX}px)`;
    this.#underline.style.width = tab.offsetWidth + 'px';
  }

  /**
   * 
   * @param {*} tab 
   * @param {*} index 
   * @description 处理标签点击
   */
  #handleTabClick(tab, index) {
    // 移除所有标签的active类
    this.#tabItems.forEach(sub => {
      sub.classList.remove('active');
    });

    // 添加active类到当前标签
    tab.classList.add('active');

    // 移动下划线
    this.#moveUnderlineTo(/** @type {HTMLElement} */(tab));

    // 切换区域显示
    const tabId = tab.getAttribute('id');
    if (tabId) {
      this.#toggleSections(tabId);
    }

    if (this.isMobile) {
      this.#scrollToCenter(tab)
    }
  }


  /**
   * 
   * @param {HTMLElement} item 
   * @description 移动端Active Tab滚动到中心位置
   * @returns 
   */
  #scrollToCenter(item) {
    const container = this
    if (!container) return;
    const containerWidth = container.offsetWidth;
    const itemLeft = item.offsetLeft;
    const itemWidth = item.offsetWidth;
    const scrollLeft = itemLeft - (containerWidth / 2) + (itemWidth / 2);

    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  }

  /**
   * 
   * @param {string} currentTabId 
   */
  #toggleSections(currentTabId) {
    // 隐藏所有区域
    this.#sectionMap.forEach((sections, key) => {
      sections.forEach(item => {
        item.style.display = 'none';
      });
    });

    // 显示当前区域
    const currentSections = this.#sectionMap.get(currentTabId);
    if (currentSections) {
      currentSections.forEach(cur => {
        if (cur.dataset.originalDisplay !== undefined) {
          cur.style.display = cur.dataset.originalDisplay;
        } else {
          cur.style.removeProperty('display');
        }
      });
    }
  }

  /**
   * 
   * @param {*} event 
   * @description 处理点击事件
   */
  handleClick(event) {
    const tabItem = event.target;
    if (!tabItem) return;
    const index = this.#tabItems.indexOf(tabItem);
    if (index !== -1) {
      this.#handleTabClick(tabItem, index);
    }
  }
}

// 注册自定义元素
if (!customElements.get('tab-section-component')) {
  customElements.define('tab-section-component', TabSection);
}